import type {
  PaymentMethod,
  PaymentMethodAction,
  PaymentMethods,
  PaymentMethodSlot,
} from "../types";

const MAX_BACKUP_METHODS = 2;

export function canAddBackupMethod(paymentMethods: PaymentMethods): boolean {
  return paymentMethods.backupMethods.length < MAX_BACKUP_METHODS;
}

export function getMethodInSlot(
  paymentMethods: PaymentMethods,
  slot: PaymentMethodSlot,
): PaymentMethod | undefined {
  return slot.kind === "default"
    ? paymentMethods.defaultMethod
    : paymentMethods.backupMethods[slot.index];
}

export function applyPaymentMethodAction(
  paymentMethods: PaymentMethods,
  action: PaymentMethodAction,
): PaymentMethods {
  const { backupMethods, defaultMethod } = paymentMethods;

  if (action.type === "add") {
    if (!canAddBackupMethod(paymentMethods)) {
      throw new Error("You already have the maximum number of backup methods.");
    }

    return action.setAsDefault
      ? {
          backupMethods: [...backupMethods, defaultMethod],
          defaultMethod: action.method,
        }
      : { backupMethods: [...backupMethods, action.method], defaultMethod };
  }

  if (action.type === "remove") {
    assertBackupExists(paymentMethods, action.index);

    return {
      backupMethods: backupMethods.filter((_, index) => index !== action.index),
      defaultMethod,
    };
  }

  if (action.slot.kind === "default") {
    return { backupMethods, defaultMethod: action.method };
  }

  const { index } = action.slot;

  assertBackupExists(paymentMethods, index);

  return action.setAsDefault
    ? {
        backupMethods: backupMethods.with(index, defaultMethod),
        defaultMethod: action.method,
      }
    : {
        backupMethods: backupMethods.with(index, action.method),
        defaultMethod,
      };
}

function assertBackupExists(paymentMethods: PaymentMethods, index: number) {
  if (!paymentMethods.backupMethods[index]) {
    throw new Error("This backup method no longer exists.");
  }
}
